import { Component, Vue } from "vue-property-decorator";
import moment from "moment";
import AppLoaderComponent from "../app-loader/app-loader.component.vue";
import ErrorPopupComponent from "../error-popup/error-popup.component.vue";
import FileUploadComponent from "../file-upload/file-upload.component.vue";
import { complaintApi } from "@/providers/apis/complaint";
import { utils } from "@/utils/utils";
import { commonService } from "@/providers/services/common-service";

/**
 * Roughly what fits the note column on one line. Past it the row offers to
 * open; under it the control would sit next to text that already fits.
 */
const COMMENT_LINE_LENGTH = 80;

/**
 * How many entries either history shows before it asks. Both lists grow for
 * the life of the complaint, so an old one would otherwise bury the form the
 * reviewer came here to use.
 */
const HISTORY_PAGE_SIZE = 5;

@Component({
  name: "reviewer-complaint-preview",
  components: {
    AppLoaderComponent,
    ErrorPopupComponent,
    FileUploadComponent,
  },
})
export default class ReviewerComplaintPreview extends Vue {
  public complaint: any = null;

  public isLoading = true;
  public isSaving = false;
  public errorMessage = "";
  public submitError = "";

  // Right-hand panel state
  public selectedPrograms: string[] = [];
  public selectedStatus = "";
  public commentText = "";
  public showProgramDropdown = false;
  public showStatusDropdown = false;

  /** Staff attachments being edited; saved with the rest on Submit. */
  public reviewerFiles: any[] = [];

  public statusOptions = [
    { text: "Received", value: "RECEIVED" },
    { text: "Reviewing", value: "REVIEWING" },
    { text: "Closed", value: "CLOSED" },
  ];

  public programOptions = [
    { text: "CPRS", value: "CPRS" },
    { text: "CFSS", value: "CFSS" },
    { text: "CYAPSS", value: "CYAPSS" },
  ];

  public get complaintId(): string {
    return this.$route.params.complaintId;
  }

  /** Programs already on the complaint, as an array (API sends comma-joined). */
  private get currentPrograms(): string[] {
    return this.complaint?.program
      ? String(this.complaint.program).split(",").filter(Boolean)
      : [];
  }

  public get isProgramChanged(): boolean {
    const a = [...this.selectedPrograms].sort().join(",");
    const b = [...this.currentPrograms].sort().join(",");
    return this.selectedPrograms.length > 0 && a !== b;
  }

  public isProgramSelected(value: string): boolean {
    return this.selectedPrograms.includes(value);
  }

  /**
   * Status is only offered once the complaint belongs to a program. A program
   * picked but not yet submitted counts, so the two can be set in one pass.
   */
  public get isStatusEnabled(): boolean {
    return this.selectedPrograms.length > 0;
  }

  /** Status change history (oldest first), from the API. */
  /** Newest first, formatted at load — see buildStatusHistory. */
  public statusHistory: any[] = [];

  /** Every staff note on the complaint, newest first. */
  public commentHistory: any[] = [];

  /**
   * Same shape as the Part One / Part Two previews: sort ascending, walk the
   * list to format and flag, then reverse so the newest event reads first.
   *
   * isArrival is set while the list is still oldest-first, so the "received
   * the complaint." label stays on the right row after the flip and when two
   * events share a timestamp.
   */
  private buildStatusHistory(history: any): any[] {
    const entries = Array.isArray(history) ? history : [];
    entries.sort(
      (a: any, b: any) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    entries.map((entry: any, index: number) => {
      entry.isArrival = index === 0;
      entry.createdAt = moment
        .utc(entry.createdAt)
        .local()
        .format("MM-DD-YYYY hh:mm A");
    });
    entries.reverse();
    return entries;
  }

  /**
   * complaint_comment is insert-only, so the API returns every note. Same
   * treatment as the status history: format in local time, newest first.
   */
  private buildCommentHistory(history: any): any[] {
    const entries = Array.isArray(history) ? [...history] : [];
    entries.sort(
      (a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return entries.map((entry: any) => ({
      ...entry,
      createdAt: moment
        .utc(entry.createdAt)
        .local()
        .format("MM-DD-YYYY hh:mm A"),
    }));
  }

  /** Notes opened in full; every other row stays on its single line. */
  public expandedComments: number[] = [];

  public showAllComments = false;
  public showAllStatus = false;

  /** The newest five, until the reviewer asks for the rest. */
  public get visibleComments(): any[] {
    return this.showAllComments
      ? this.commentHistory
      : this.commentHistory.slice(0, HISTORY_PAGE_SIZE);
  }

  public get visibleStatusHistory(): any[] {
    return this.showAllStatus
      ? this.statusHistory
      : this.statusHistory.slice(0, HISTORY_PAGE_SIZE);
  }

  public get hiddenCommentCount(): number {
    return Math.max(0, this.commentHistory.length - HISTORY_PAGE_SIZE);
  }

  public get hiddenStatusCount(): number {
    return Math.max(0, this.statusHistory.length - HISTORY_PAGE_SIZE);
  }

  public isCommentOpen(index: number): boolean {
    return this.expandedComments.includes(index);
  }

  public toggleComment(index: number) {
    this.expandedComments = this.isCommentOpen(index)
      ? this.expandedComments.filter((open: number) => open !== index)
      : [...this.expandedComments, index];
  }

  /**
   * Only a note too long for its line offers to open. Measured on the text
   * rather than the rendered width: the panel is a fixed column, so the
   * character count is a good enough stand-in and costs no layout read.
   */
  public isCommentLong(entry: any): boolean {
    return `${(entry && entry.comment) || ""}`.length > COMMENT_LINE_LENGTH;
  }

  public historyName(entry: any): string {
    const name = `${entry.firstName || ""} ${entry.lastName || ""}`.trim();
    return name || "System";
  }

  public get isStatusChanged(): boolean {
    return (
      !!this.selectedStatus &&
      this.selectedStatus !== (this.complaint?.status || "")
    );
  }

  /** Staff attachments already on the complaint. */
  public get savedReviewerFiles(): any[] {
    const raw = this.complaint?.reviewerAttachment;
    if (!raw) {
      return [];
    }
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** Seeds the shared uploader with what is already on the complaint. */
  public get reviewerAttachmentData(): any {
    return {
      payloadName: "reviewer-documents",
      files: this.reviewerFiles,
    };
  }

  /**
   * Only files that made it to storage. The uploader keeps a raw File in its
   * list while one is in flight, and a File has no fileName to persist.
   */
  public get storedReviewerFiles(): any[] {
    return this.reviewerFiles.filter(
      (file: any) => file && !(file instanceof File) && file.fileName
    );
  }

  public get isReviewerFilesChanged(): boolean {
    const key = (list: any[]) =>
      list.map((file: any) => file.fileName).join("|");
    return key(this.storedReviewerFiles) !== key(this.savedReviewerFiles);
  }

  public get isCommentChanged(): boolean {
    return this.commentText.trim() !== (this.complaint?.comment || "").trim();
  }

  /** One Submit applies whichever of program / status / comment changed. */
  public get hasChanges(): boolean {
    return (
      this.isProgramChanged ||
      this.isStatusChanged ||
      this.isCommentChanged ||
      this.isReviewerFilesChanged
    );
  }

  /**
   * A closed complaint is final: the saved status is CLOSED, so program,
   * status and comment are locked and Submit is disabled.
   */
  public get isClosed(): boolean {
    return (this.complaint?.status || "").toUpperCase() === "CLOSED";
  }

  /** Supporting documents fed to the file component for in-popup preview. */
  public get documentsAttachment(): any {
    return { payloadName: "documents", files: this.attachments };
  }

  public get attachments(): any[] {
    const raw = this.complaint?.attachment;
    if (!raw) {
      return [];
    }
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  public created() {
    this.loadComplaint();
  }

  public loadComplaint() {
    this.isLoading = true;
    complaintApi
      .getComplaint(this.complaintId)
      .then((data: any) => {
        this.isLoading = false;
        this.complaint = data;
        this.statusHistory = this.buildStatusHistory(data.statusHistory);
        this.commentHistory = this.buildCommentHistory(data.commentHistory);
        this.selectedPrograms = data.program
          ? String(data.program).split(",").filter(Boolean)
          : [];
        this.selectedStatus = data.status || "";
        // Prefill the single running comment so staff edit it in place.
        this.commentText = data.comment || "";
        // Copy, not reference: the working list is compared against the saved
        // one to decide whether Submit has anything to do.
        this.reviewerFiles = [...this.savedReviewerFiles];
      })
      .catch((error: any) => {
        this.isLoading = false;
        this.errorMessage = utils.errorMessage(error);
      });
  }

  public formatDate(value: string): string {
    return value ? moment(value).format("MM-DD-YYYY") : "—";
  }

  public statusText(status: string): string {
    const match = this.statusOptions.find((option) => option.value === status);
    return match ? match.text : "";
  }

  /** Reuses the training-status dot classes so the pill reads the same. */
  public statusDotClass(status: string): string {
    const value = (status || "").toUpperCase();
    if (value === "REVIEWING") return "ts-progress";
    if (value === "CLOSED") return "ts-completed";
    if (value === "RECEIVED") return "ts-notstarted";
    return "";
  }

  /** Program dropdown is inert once the complaint is closed. */
  public toggleProgramDropdown() {
    if (this.isClosed) {
      return;
    }
    this.showProgramDropdown = !this.showProgramDropdown;
  }

  /** Multi-select: toggle a program in/out of the selection. */
  public toggleProgram(value: string) {
    if (this.isClosed) {
      return;
    }
    const idx = this.selectedPrograms.indexOf(value);
    if (idx === -1) {
      this.selectedPrograms.push(value);
    } else {
      this.selectedPrograms.splice(idx, 1);
    }
  }

  public toggleStatusDropdown() {
    if (!this.isStatusEnabled || this.isClosed) {
      return;
    }
    this.showStatusDropdown = !this.showStatusDropdown;
  }

  public selectStatus(option: any) {
    this.selectedStatus = option.value;
    this.showStatusDropdown = false;
  }

  /**
   * One call carrying only what changed. The API applies program, status and
   * comment in a single transaction, so a failure leaves the complaint exactly
   * as it was rather than partly updated.
   */
  public submitReview() {
    if (!this.hasChanges || this.isSaving || this.isClosed) {
      return;
    }
    this.isSaving = true;
    this.submitError = "";

    const changes: {
      program?: string;
      status?: string;
      comment?: string;
      reviewerAttachment?: any[];
    } = {};
    if (this.isProgramChanged) {
      changes.program = this.selectedPrograms.join(",");
    }
    if (this.isStatusChanged) {
      changes.status = this.selectedStatus;
    }
    if (this.isCommentChanged) {
      changes.comment = this.commentText.trim();
    }
    // Sent whole, so removing the last document still reaches the server as an
    // empty array rather than looking like "no change to attachments".
    if (this.isReviewerFilesChanged) {
      changes.reviewerAttachment = this.storedReviewerFiles;
    }

    complaintApi
      .updateComplaint(this.complaintId, changes)
      .then(() => {
        this.isSaving = false;
        this.loadComplaint();
      })
      .catch((error: any) => {
        this.isSaving = false;
        this.submitError = utils.errorMessage(error);
        this.loadComplaint();
      });
  }

  public onBack() {
    this.$router.push({ path: "/reviewer-dashboard/complaints" });
  }

  // Event name matches ErrorPopupComponent's existing (misspelled) emit.
  public closdedErrorPopup(event: boolean) {
    if (event) {
      this.errorMessage = "";
    }
  }
}
